// src/services/communityService.ts
import { db, now } from "../firebase/firestore";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  getDocs,
  setDoc,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import {
  getDownloadURL,
  getMetadata,
  listAll,
  ref as storageRef,
  deleteObject,
} from "firebase/storage";
import { storage } from "../firebase/storage";

import type {
  CommunityPost,
  PostCounters,
  ReactionDoc,
  CommentDoc,
} from "../models/community";

export interface Page<T> {
  items: T[];
  next: QueryDocumentSnapshot<DocumentData, DocumentData> | null;
}

/* ───────── util ───────── */
function readCounters(d: any): PostCounters {
  const c = d?.counters ?? {};
  const like = typeof c.like === "number" ? c.like : 0;
  const recommend = typeof c.recommend === "number" ? c.recommend : 0;
  const comment = typeof c.comment === "number" ? c.comment : 0;
  return { like, recommend, comment };
}

/* ───────── posts ───────── */
export async function listPostsPage(
  pageSize = 20,
  cursor?: QueryDocumentSnapshot<DocumentData, DocumentData>
): Promise<Page<CommunityPost>> {
  const ref = collection(db, "communityPosts");
  const q = cursor
    ? query(
        ref,
        orderBy("createdAt", "desc"),
        startAfter(cursor),
        limit(pageSize)
      )
    : query(ref, orderBy("createdAt", "desc"), limit(pageSize));

  const snap = await getDocs(q);
  const items: CommunityPost[] = snap.docs.map((d) => {
    const data = d.data();
    const post: CommunityPost = {
      id: d.id,
      authorUid: String(data.authorUid ?? ""),
      counters: readCounters(data),
    };
    if (typeof data.text === "string") post.text = data.text;
    if (typeof data.visibility === "string") post.visibility = data.visibility;
    if (data.createdAt !== undefined) post.createdAt = data.createdAt;
    if (data.updatedAt !== undefined) post.updatedAt = data.updatedAt;
    return post;
  });

  let next: QueryDocumentSnapshot<DocumentData, DocumentData> | null = null;
  if (snap.docs.length === pageSize && snap.docs.length > 0) {
    next = snap.docs[snap.docs.length - 1] as QueryDocumentSnapshot<
      DocumentData,
      DocumentData
    >;
  }
  return { items, next };
}

export async function getPost(postId: string): Promise<CommunityPost | null> {
  const d = await getDoc(doc(db, "communityPosts", postId));
  if (!d.exists()) return null;
  const data = d.data();
  const post: CommunityPost = {
    id: d.id,
    authorUid: String(data.authorUid ?? ""),
    counters: readCounters(data),
  };
  if (typeof data.text === "string") post.text = data.text;
  if (typeof data.visibility === "string") post.visibility = data.visibility;
  if (data.createdAt !== undefined) post.createdAt = data.createdAt;
  if (data.updatedAt !== undefined) post.updatedAt = data.updatedAt;
  return post;
}

export async function upsertPost(
  input: { id: string; text: string } | { authorUid: string; text: string }
): Promise<CommunityPost> {
  if ("id" in input) {
    const postRef = doc(db, "communityPosts", input.id);
    await updateDoc(postRef, { text: input.text, updatedAt: now() });
    const d = await getDoc(postRef);
    const data = d.data()!;
    return {
      id: d.id,
      authorUid: data.authorUid,
      text: data.text,
      counters: readCounters(data),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };
  }
  // create
  const ref = collection(db, "communityPosts");
  const d = await addDoc(ref, {
    authorUid: input.authorUid,
    text: input.text,
    counters: { like: 0, recommend: 0, comment: 0 },
    createdAt: now(),
    updatedAt: now(),
  });
  const snap = await getDoc(d);
  const data = snap.data()!;
  return {
    id: snap.id,
    authorUid: data.authorUid,
    text: data.text,
    counters: readCounters(data),
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

export async function deletePost(postId: string): Promise<void> {
  await deleteDoc(doc(db, "communityPosts", postId));
}

/* ───────── reacciones ───────── */
export async function getReaction(
  postId: string,
  uid: string
): Promise<ReactionDoc | null> {
  const r = await getDoc(doc(db, "communityPosts", postId, "reactions", uid));
  return r.exists() ? (r.data() as ReactionDoc) : null;
}

/** Toggle seguro. Calcula delta 0/±1 con base en el estado previo. */
export async function setReaction(
  postId: string,
  uid: string,
  next: { like?: boolean; recommend?: boolean }
): Promise<void> {
  const postRef = doc(db, "communityPosts", postId);
  const reactRef = doc(db, "communityPosts", postId, "reactions", uid);

  const [postSnap, reactSnap] = await Promise.all([
    getDoc(postRef),
    getDoc(reactRef),
  ]);
  if (!postSnap.exists()) return;

  const prevLike =
    reactSnap.exists() && typeof reactSnap.data().like === "boolean"
      ? !!reactSnap.data().like
      : false;
  const prevRec =
    reactSnap.exists() && typeof reactSnap.data().recommend === "boolean"
      ? !!reactSnap.data().recommend
      : false;

  const newLike = typeof next.like === "boolean" ? next.like : prevLike;
  const newRec = typeof next.recommend === "boolean" ? next.recommend : prevRec;

  const deltaLike = (newLike ? 1 : 0) - (prevLike ? 1 : 0);
  const deltaRec = (newRec ? 1 : 0) - (prevRec ? 1 : 0);

  // payload como DocumentData para evitar errores de tipos
  const payload: DocumentData = {
    like: newLike,
    recommend: newRec,
    updatedAt: now(),
  };

  await setDoc(reactRef, payload, { merge: true });

  const counters = postSnap.data().counters ?? {};
  await updateDoc(postRef, {
    counters: {
      like: (counters.like ?? 0) + deltaLike,
      recommend: (counters.recommend ?? 0) + deltaRec,
      comment: counters.comment ?? 0,
    },
    updatedAt: now(),
  });
}

/* ───────── comentarios ───────── */
export async function listComments(postId: string): Promise<CommentDoc[]> {
  const ref = collection(db, "communityPosts", postId, "comments");
  const snap = await getDocs(
    query(ref, orderBy("createdAt", "asc"), limit(500))
  );
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      uid: String(data.uid ?? ""),
      text: String(data.text ?? ""),
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };
  });
}

export async function addComment(
  postId: string,
  uid: string,
  text: string
): Promise<void> {
  const ref = collection(db, "communityPosts", postId, "comments");
  await addDoc(ref, { uid, text, createdAt: now(), updatedAt: now() });

  const postRef = doc(db, "communityPosts", postId);
  const post = await getDoc(postRef);
  if (!post.exists()) return;
  const counters = readCounters(post.data());
  await updateDoc(postRef, {
    counters: { ...counters, comment: counters.comment + 1 },
    updatedAt: now(),
  });
}

export async function editComment(
  postId: string,
  commentId: string,
  text: string
): Promise<void> {
  await updateDoc(doc(db, "communityPosts", postId, "comments", commentId), {
    text,
    updatedAt: now(),
  });
}

export async function deleteComment(
  postId: string,
  commentId: string
): Promise<void> {
  const postRef = doc(db, "communityPosts", postId);
  await deleteDoc(doc(db, "communityPosts", postId, "comments", commentId));

  const post = await getDoc(postRef);
  if (!post.exists()) return;
  const counters = readCounters(post.data());
  const nextCount = Math.max(0, counters.comment - 1);
  await updateDoc(postRef, {
    counters: { ...counters, comment: nextCount },
    updatedAt: now(),
  });
}

/* ───────── media (Storage + Firestore media array) ───────── */
export type MediaItem = {
  path: string; // si no conocemos el path, colocamos el downloadURL (se maneja en deleteMedia)
  name: string;
  downloadURL: string;
  contentType: string;
  kind: "image" | "video";
  updated?: Date;
  size?: number;
};

function kindFromCTorURL(
  contentType: string | null | undefined,
  url: string
): "image" | "video" {
  if (contentType?.startsWith("video/")) return "video";
  if (contentType?.startsWith("image/")) return "image";
  const lower = url.toLowerCase();
  if (/\.(mp4|mov|webm|mkv|avi)(\?|$)/.test(lower)) return "video";
  return "image";
}

function nameFromPathOrURL(path?: string, url?: string): string {
  if (path && path.includes("/")) return path.split("/").pop() as string;
  if (url) {
    try {
      const u = new URL(url);
      const last = u.pathname.split("/").pop() || "";
      return last || "archivo";
    } catch {
      // no es url válida
    }
  }
  return path || "archivo";
}

/** Lee media desde el campo `media` del documento (si existe y es array) */
async function listMediaFromDoc(postId: string): Promise<MediaItem[]> {
  const d = await getDoc(doc(db, "communityPosts", postId));
  if (!d.exists()) return [];
  const data = d.data() as any;
  const arr: any[] = Array.isArray(data.media) ? data.media : [];
  const out: MediaItem[] = [];
  for (const m of arr) {
    const url = typeof m?.downloadURL === "string" ? m.downloadURL : "";
    if (!url) continue;
    const ct = typeof m?.contentType === "string" ? m.contentType : null;
    const path =
      typeof m?.path === "string" && m.path.length > 0 ? m.path : url; // si no hay path, usamos url
    const name =
      typeof m?.name === "string" && m.name.length > 0
        ? m.name
        : nameFromPathOrURL(m?.path, url);
    const updated =
      m?.updatedAt && typeof m.updatedAt?.toDate === "function"
        ? (m.updatedAt.toDate() as Date)
        : undefined;
    const size =
      typeof m?.size === "number" && Number.isFinite(m.size)
        ? (m.size as number)
        : undefined;

    out.push({
      path,
      name,
      downloadURL: url,
      contentType: ct ?? "application/octet-stream",
      kind: kindFromCTorURL(ct, url),
      ...(updated ? { updated } : {}),
      ...(size !== undefined ? { size } : {}),
    });
  }
  return out;
}

/** Lista archivos en una carpeta concreta (si no existe devuelve []) */
async function listFolder(prefix: string): Promise<MediaItem[]> {
  try {
    const res = await listAll(storageRef(storage, prefix));
    const acc: MediaItem[] = [];
    for (const item of res.items) {
      const [url, meta] = await Promise.all([
        getDownloadURL(item),
        getMetadata(item),
      ]);
      const ct = meta.contentType ?? "application/octet-stream";
      const upd = meta.updated ? new Date(meta.updated) : undefined;
      const size = typeof meta.size === "number" ? meta.size : undefined;

      acc.push({
        path: item.fullPath,
        name: item.name,
        downloadURL: url,
        contentType: ct,
        kind: kindFromCTorURL(ct, url),
        ...(upd ? { updated: upd } : {}),
        ...(size !== undefined ? { size } : {}),
      });
    }
    return acc;
  } catch {
    return [];
  }
}

/** Lista imágenes y videos:
 *  1) Si el post tiene campo `media` en Firestore, se usa.
 *  2) Si no hay `media`, se listan objetos en Storage (raíz + /images + /videos).
 */
export async function listPostMedia(
  ownerUid: string,
  postId: string
): Promise<MediaItem[]> {
  // 1) Intentar leer el array media del documento (como está en tu screenshot)
  const fromDoc = await listMediaFromDoc(postId);
  if (fromDoc.length > 0) {
    // Ordenar por updated si existe
    fromDoc.sort((a, b) => {
      const ta = a.updated ? a.updated.getTime() : 0;
      const tb = b.updated ? b.updated.getTime() : 0;
      return tb - ta;
    });
    return fromDoc;
  }

  // 2) Fallback a Storage (por si hay posts más viejos o subidos sin array)
  const base = `community/${ownerUid}/${postId}`;
  const [atRoot, atImages, atVideos] = await Promise.all([
    listFolder(base),
    listFolder(`${base}/images`),
    listFolder(`${base}/videos`),
  ]);

  const all = [...atRoot, ...atImages, ...atVideos];

  // Deduplicar por downloadURL o path
  const seen = new Set<string>();
  const dedup: MediaItem[] = [];
  for (const m of all) {
    const key = m.downloadURL || m.path;
    if (seen.has(key)) continue;
    seen.add(key);
    dedup.push(m);
  }

  dedup.sort((a, b) => {
    const ta = a.updated ? a.updated.getTime() : 0;
    const tb = b.updated ? b.updated.getTime() : 0;
    return tb - ta;
  });
  return dedup;
}

/** Acepta path de Storage (ej: 'community/.../file.jpg') o un downloadURL */
export async function deleteMedia(pathOrURL: string): Promise<void> {
  // Si viene un downloadURL (porque en el doc sólo tenemos eso), también funciona:
  await deleteObject(storageRef(storage, pathOrURL));
}
